import { useCallback, useEffect, useRef, useState } from "react"
import { loadVideoTemplatePage } from "~/common/videoTemplate/catalog"
import type { VideoTemplate } from "~/common/videoTemplate/types"
import { useVideoTemplatesStore } from "~/store/videoTemplates"

const TEMPLATE_GALLERY_PAGE_SIZE = 20

type GalleryState = {
  category: string
  templates: VideoTemplate[]
  total: number
  status: "loading" | "ready" | "error"
  error: string
  loadingMore: boolean
  moreError: string
}

type Session = {
  category: string
  controller: AbortController
  templates: VideoTemplate[]
  total?: number
  page: number
  loading: boolean
  failed: boolean
}

const initialState = (category: string): GalleryState => ({
  category,
  templates: [],
  total: 0,
  status: "loading",
  error: "",
  loadingMore: false,
  moreError: "",
})

export const useTemplateGallery = () => {
  const cacheTemplates = useVideoTemplatesStore((state) => state.cacheTemplates)
  // 单选分类，为空表示全部；分类变更后丢弃过期请求结果
  const [category, setCategory] = useState("")
  const [revision, setRevision] = useState(0)
  const [state, setState] = useState<GalleryState>(() => initialState(category))
  const session = useRef<Session | null>(null)

  const requestPage = useCallback(
    async (current: Session) => {
      if (current.loading || current.controller.signal.aborted) return
      current.loading = true
      current.failed = false
      const first = current.page === 0
      setState((previous) => ({
        ...previous,
        loadingMore: !first,
        moreError: "",
      }))
      try {
        const { items, total } = await loadVideoTemplatePage(
          {
            // 接口按 categories 筛选，用户侧一次只选一个分类
            ...(current.category ? { categories: [current.category] } : {}),
            page: current.page,
            page_size: TEMPLATE_GALLERY_PAGE_SIZE,
          },
          { signal: current.controller.signal },
        )
        if (current.controller.signal.aborted) return
        if (current.total !== undefined && total !== current.total)
          throw new Error("模板列表已变化，请重新加载列表")
        const expected = Math.min(
          TEMPLATE_GALLERY_PAGE_SIZE,
          total - current.templates.length,
        )
        if (expected < 0 || items.length !== expected)
          throw new Error("模板列表分页数据不完整，请重新加载列表")
        const ids = new Set(current.templates.map((item) => item.templateId))
        if (items.some((item) => ids.has(item.templateId)))
          throw new Error("模板列表已变化或存在重复标识，请重新加载列表")
        current.templates = [...current.templates, ...items]
        current.total = total
        current.page++
        cacheTemplates(items)
        setState({
          category: current.category,
          templates: current.templates,
          total,
          status: "ready",
          error: "",
          loadingMore: false,
          moreError: "",
        })
      } catch (cause) {
        if (current.controller.signal.aborted) return
        current.failed = true
        const error =
          cause instanceof Error ? cause.message : "模板加载失败，请重试"
        setState((previous) => ({
          ...previous,
          status: first ? "error" : "ready",
          error: first ? error : "",
          loadingMore: false,
          moreError: first ? "" : error,
        }))
      } finally {
        current.loading = false
      }
    },
    [cacheTemplates],
  )

  useEffect(() => {
    const current: Session = {
      category,
      controller: new AbortController(),
      templates: [],
      page: 0,
      loading: false,
      failed: false,
    }
    session.current = current
    setState(initialState(category))
    void requestPage(current)
    return () => current.controller.abort()
  }, [category, revision, requestPage])

  const loadMore = useCallback(() => {
    const current = session.current
    if (
      !current ||
      current.category !== category ||
      current.failed ||
      current.total === undefined ||
      current.templates.length >= current.total
    )
      return
    void requestPage(current)
  }, [category, requestPage])

  const retryMore = useCallback(() => {
    const current = session.current
    if (current?.category === category && current.failed && current.page > 0)
      void requestPage(current)
  }, [category, requestPage])

  const list = state.category === category ? state : initialState(category)
  return {
    ...list,
    category,
    setCategory,
    loadMore,
    retryMore,
    hasMore: list.status === "ready" && list.templates.length < list.total,
    reload: () => setRevision((value) => value + 1),
  }
}
